import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { FloatingLabelInput } from '../components/FloatingLabelInput';
import { sanitizeName } from '../../lib/validators';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { ThemedDatePicker } from '../components/ThemedDatePicker';
import { PrimaryButton } from '../components/PrimaryButton';
import { ConfirmModal } from '../components/ConfirmModal';
import {
  customerApiErrorMessage,
  getMe,
  updateMe,
  uploadFile,
  listMyAddresses,
  addMyAddress,
  updateMyAddress,
  deleteMyAddress,
  setDefaultMyAddress,
  type CustomerSavedAddress,
  type SavedAddressInput,
} from '../../lib/customer-api';
import { patchCustomer } from '../../lib/customer-auth';
import { RequireCustomer } from '../../lib/use-customer-auth';
import { toast } from '../../lib/toast';

const GENDER_OPTIONS = ['', 'FEMALE', 'MALE', 'PREFER_NOT_TO_SAY'];
const GENDER_LABEL: Record<string, string> = {
  '': '',
  FEMALE: 'Female',
  MALE: 'Male',
  PREFER_NOT_TO_SAY: 'Prefer not to say',
};

export default function AccountInfoPage() {
  return (
    <RequireCustomer>
      <AccountInfoInner />
    </RequireCustomer>
  );
}

function AccountInfoInner() {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    gender: '' as '' | 'FEMALE' | 'MALE' | 'PREFER_NOT_TO_SAY',
    date_of_birth: '',
    mobile_number: '',
    dial_code: '+44',
    profile_pic: '',
    // Server-supplied short-lived signed URL we render in <img>. The
    // canonical `profile_pic` is what we send back on save.
    profile_pic_preview: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // ----- Address book (multiple saved addresses) -----
  const emptyAddressDraft = {
    label: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    country_code: '',
    // Populated by the Places picker when address_line1 is chosen. Save
    // requires both so the booking radius search can include this address.
    latitude: null as number | null,
    longitude: null as number | null,
  };

  const [addresses, setAddresses] = useState<CustomerSavedAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  // null = form hidden; 'new' = adding; an id = editing that address.
  const [addressFormMode, setAddressFormMode] = useState<'new' | string | null>(
    null,
  );
  const [addressDraft, setAddressDraft] = useState(emptyAddressDraft);
  const [savingAddress, setSavingAddress] = useState(false);
  // Address id pending delete-confirmation, or null.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingAddress, setDeletingAddress] = useState(false);
  // Address id whose default/delete action is in flight (disables its row).
  const [addressBusyId, setAddressBusyId] = useState<string | null>(null);

  const loadAddresses = async () => {
    setAddressesLoading(true);
    try {
      const res = await listMyAddresses();
      setAddresses(res.info ?? []);
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not load your saved addresses.'),
      );
    } finally {
      setAddressesLoading(false);
    }
  };

  const openAddAddress = () => {
    setAddressDraft(emptyAddressDraft);
    setAddressFormMode('new');
  };

  const openEditAddress = (a: CustomerSavedAddress) => {
    setAddressDraft({
      label: a.label ?? '',
      address_line1: a.address_line1 ?? '',
      address_line2: a.address_line2 ?? '',
      city: a.city ?? '',
      postcode: a.postcode ?? '',
      country_code: a.country_code ?? '',
      latitude: a.latitude ?? null,
      longitude: a.longitude ?? null,
    });
    setAddressFormMode(a.id);
  };

  const closeAddressForm = () => {
    setAddressFormMode(null);
    setAddressDraft(emptyAddressDraft);
  };

  const handleSaveAddress = async () => {
    if (!addressDraft.address_line1.trim()) {
      toast.error('Enter an address.');
      return;
    }
    // Address line 1 only counts as captured if it came from the address
    // picker (lat/lng populated). Block save otherwise — same rule the
    // old single-address edit used — so the booking radius search works.
    const { latitude, longitude } = addressDraft;
    if (latitude == null || longitude == null) {
      toast.error(
        'Pick your address from the suggestions so we can save its location.',
      );
      return;
    }
    // City + postcode are required. They auto-fill from the picked
    // address, but the picker doesn't always return both, so we surface
    // them as editable fields and block save until they're present.
    if (!addressDraft.city.trim()) {
      toast.error('Enter the town / city.');
      return;
    }
    if (!addressDraft.postcode.trim()) {
      toast.error('Enter the postcode.');
      return;
    }
    setSavingAddress(true);
    try {
      const payload: SavedAddressInput = {
        label: addressDraft.label.trim() || null,
        address_line1: addressDraft.address_line1.trim(),
        address_line2: addressDraft.address_line2.trim() || null,
        city: addressDraft.city.trim() || null,
        postcode: addressDraft.postcode.trim() || null,
        country_code: addressDraft.country_code.trim() || null,
        latitude,
        longitude,
      };
      if (addressFormMode === 'new') {
        await addMyAddress(payload);
        toast.success('Address added.');
      } else if (addressFormMode) {
        await updateMyAddress(addressFormMode, payload);
        toast.success('Address updated.');
      }
      closeAddressForm();
      await loadAddresses();
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not save the address.'),
      );
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSetDefaultAddress = async (id: string) => {
    setAddressBusyId(id);
    try {
      await setDefaultMyAddress(id);
      await loadAddresses();
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not set the default address.'),
      );
    } finally {
      setAddressBusyId(null);
    }
  };

  const handleConfirmDeleteAddress = async () => {
    if (!pendingDeleteId) return;
    setDeletingAddress(true);
    try {
      await deleteMyAddress(pendingDeleteId);
      if (addressFormMode === pendingDeleteId) closeAddressForm();
      toast.success('Address removed.');
      setPendingDeleteId(null);
      await loadAddresses();
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not remove the address.'),
      );
    } finally {
      setDeletingAddress(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMe();
      const u = res.info?.user ?? {};
      const p = res.info ?? {};
      setForm({
        first_name: u.first_name ?? '',
        last_name: u.last_name ?? '',
        email: u.email ?? '',
        gender: (p.gender ?? '') as any,
        date_of_birth: p.date_of_birth
          ? new Date(p.date_of_birth).toISOString().slice(0, 10)
          : '',
        mobile_number: u.mobile_number ?? '',
        dial_code: u.dial_code ?? '+44',
        profile_pic: u.profile_pic ?? '',
        profile_pic_preview:
          u.profile_pic_preview_url ?? u.profile_pic ?? '',
      });
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not load your profile.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadAddresses();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMe({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        mobile_number: form.mobile_number.trim() || null,
        dial_code: form.dial_code.trim() || null,
        gender: form.gender || null,
        date_of_birth: form.date_of_birth || null,
      });
      // Mirror locally-cached customer so header / sidebar updates
      // without a re-fetch.
      patchCustomer({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
      });
      setEditingField(null);
      toast.success('Profile updated.');
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not save your profile.');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleEdit = (field: string) =>
    setEditingField((prev) => (prev === field ? null : field));
  const isEditing = (field: string) => editingField === field;

  const stripS3QueryString = (url: string): string => {
    const q = url.indexOf('?');
    return q >= 0 ? url.slice(0, q) : url;
  };

  const handleAvatarPick = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const filesArr = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (filesArr.length === 0) return;
    const file = filesArr[0];
    const localBlob = URL.createObjectURL(file);
    setForm((prev) => ({ ...prev, profile_pic_preview: localBlob }));
    setUploadingAvatar(true);
    try {
      const res = await uploadFile(file, 'ADMIN_AVATAR');
      const canonical = stripS3QueryString(res.url);
      await updateMe({ profile_pic: canonical });
      setForm((prev) => ({ ...prev, profile_pic: canonical }));
      patchCustomer({ profile_pic: canonical } as any);
      toast.success('Profile photo updated.');
    } catch (err) {
      URL.revokeObjectURL(localBlob);
      toast.error(customerApiErrorMessage(err, 'Could not upload your photo.'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarRemove = async () => {
    if (uploadingAvatar) return;
    setUploadingAvatar(true);
    try {
      await updateMe({ profile_pic: null });
      setForm((prev) => ({ ...prev, profile_pic: '', profile_pic_preview: '' }));
      patchCustomer({ profile_pic: null } as any);
      toast.success('Profile photo removed.');
    } catch (err) {
      toast.error(customerApiErrorMessage(err, 'Could not remove your photo.'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const initials = (() => {
    const f = form.first_name?.trim()?.[0] ?? '';
    const l = form.last_name?.trim()?.[0] ?? '';
    const ini = (f + l).toUpperCase();
    return ini || form.email?.[0]?.toUpperCase() || '?';
  })();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-12">
        <div className="flex items-start gap-4 mb-12">
          <Link
            to="/account-settings"
            className="mt-1 w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors"
          >
            <span className="material-icons text-[#908e8e] text-[18px]">
              chevron_left
            </span>
          </Link>
          <div>
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-1">
              Account information
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              Profile, contact details, and address
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-[#908e8e] text-[14px] py-12">
            Loading your profile…
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row justify-between gap-12">
            <div className="flex-1 max-w-[553px]">
              <div className="space-y-8">
                {/* Profile photo */}
                <div className="flex items-center gap-5">
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarPick}
                  />
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="relative w-[88px] h-[88px] rounded-full bg-[#3a3335]/[0.04] flex items-center justify-center overflow-hidden border-2 border-[#eee] hover:border-[#8e2a2f] transition-colors disabled:opacity-60"
                    title="Change profile photo"
                    aria-label="Change profile photo"
                  >
                    {form.profile_pic_preview ? (
                      <img
                        src={form.profile_pic_preview}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[28px]">
                        {initials}
                      </span>
                    )}
                    {uploadingAvatar ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
                        <span className="material-icons animate-spin text-[20px]">
                          progress_activity
                        </span>
                      </div>
                    ) : null}
                  </button>

                  <div className="flex flex-col gap-1">
                    <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px]">
                      Profile photo
                    </p>
                    <p className="text-[#908e8e] text-[12px] mb-1">
                      JPG, PNG or WebP — up to 3 MB.
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => avatarInputRef.current?.click()}
                        disabled={uploadingAvatar}
                        className="h-[34px] px-4 rounded-[8px] bg-[#8e2a2f] text-white text-[12px] font-bold hover:bg-[#7a2228] transition-colors disabled:opacity-60"
                      >
                        {uploadingAvatar
                          ? 'Uploading…'
                          : form.profile_pic
                            ? 'Change'
                            : 'Upload'}
                      </button>
                      {form.profile_pic ? (
                        <button
                          type="button"
                          onClick={handleAvatarRemove}
                          disabled={uploadingAvatar}
                          className="h-[34px] px-4 rounded-[8px] border border-[#eee] text-[#908e8e] text-[12px] font-bold hover:text-[#8e2a2f] hover:border-[#8e2a2f] transition-colors disabled:opacity-60"
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FloatingLabelInput
                    label="First name *"
                    value={form.first_name}
                    onChange={(v) =>
                      setForm({ ...form, first_name: sanitizeName(v) })
                    }
                    containerHeight="h-[60px]"
                    bottomOnly={true}
                    disabled={!isEditing('first_name')}
                    rightAction={{
                      icon: isEditing('first_name') ? 'check' : 'edit',
                      label: isEditing('first_name') ? 'Done' : 'Edit',
                      onClick: () => toggleEdit('first_name'),
                    }}
                  />
                  <FloatingLabelInput
                    label="Last name *"
                    value={form.last_name}
                    onChange={(v) =>
                      setForm({ ...form, last_name: sanitizeName(v) })
                    }
                    containerHeight="h-[60px]"
                    bottomOnly={true}
                    disabled={!isEditing('last_name')}
                    rightAction={{
                      icon: isEditing('last_name') ? 'check' : 'edit',
                      label: isEditing('last_name') ? 'Done' : 'Edit',
                      onClick: () => toggleEdit('last_name'),
                    }}
                  />
                </div>

                {/* Gender — themed select with custom chevron so the
                    closed control matches the rest of the form instead
                    of the raw OS dropdown look. */}
                <div>
                  <label className="text-xs font-bold text-[#908e8e] block mb-1">
                    Gender
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <select
                        value={form.gender}
                        onChange={(e) =>
                          setForm({ ...form, gender: e.target.value as any })
                        }
                        disabled={!isEditing('gender')}
                        className="h-[50px] w-full rounded-[8px] border border-[#eee] bg-white pl-4 pr-10 text-[14px] font-bold text-[#3a3335] outline-none appearance-none cursor-pointer focus:border-[#8e2a2f] focus:ring-1 focus:ring-[#8e2a2f] disabled:bg-[#fafafa] disabled:text-[#908e8e] disabled:cursor-not-allowed"
                      >
                        {GENDER_OPTIONS.map((g) => (
                          <option key={g} value={g}>
                            {GENDER_LABEL[g] || '— Select —'}
                          </option>
                        ))}
                      </select>
                      <span className="material-icons absolute right-3 top-1/2 -translate-y-1/2 text-[#908e8e] pointer-events-none text-[20px]">
                        keyboard_arrow_down
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleEdit('gender')}
                      className="flex items-center gap-2 text-[#908e8e] hover:text-[#3a3335] transition-colors font-['DM_Sans',sans-serif] text-[14px] shrink-0"
                    >
                      <span className="material-icons !text-[18px]">
                        {isEditing('gender') ? 'check' : 'edit'}
                      </span>
                      <span className="font-medium">
                        {isEditing('gender') ? 'Done' : 'Edit'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Date of birth — themed date picker (calendar) instead
                    of a free-text field. Capped at today. */}
                <div>
                  <label className="text-xs font-bold text-[#908e8e] block mb-1">
                    Date of birth
                  </label>
                  <div className="flex items-center gap-2">
                    <ThemedDatePicker
                      value={form.date_of_birth}
                      onChange={(v) => setForm({ ...form, date_of_birth: v })}
                      disabled={!isEditing('date_of_birth')}
                      max={new Date().toISOString().slice(0, 10)}
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => toggleEdit('date_of_birth')}
                      className="flex items-center gap-2 text-[#908e8e] hover:text-[#3a3335] transition-colors font-['DM_Sans',sans-serif] text-[14px] shrink-0"
                    >
                      <span className="material-icons !text-[18px]">
                        {isEditing('date_of_birth') ? 'check' : 'edit'}
                      </span>
                      <span className="font-medium">
                        {isEditing('date_of_birth') ? 'Done' : 'Edit'}
                      </span>
                    </button>
                  </div>
                </div>

                <FloatingLabelInput
                  label="Email address *"
                  type="email"
                  value={form.email}
                  onChange={() => undefined}
                  containerHeight="h-[60px]"
                  bottomOnly={true}
                  disabled={true}
                />

                <div className="grid grid-cols-[100px_1fr] gap-4">
                  <FloatingLabelInput
                    label="Dial code"
                    value={form.dial_code}
                    onChange={(v) => setForm({ ...form, dial_code: v })}
                    containerHeight="h-[60px]"
                    bottomOnly={true}
                    disabled={!isEditing('mobile_number')}
                  />
                  <FloatingLabelInput
                    label="Mobile number"
                    value={form.mobile_number}
                    onChange={(v) => setForm({ ...form, mobile_number: v })}
                    containerHeight="h-[60px]"
                    bottomOnly={true}
                    disabled={!isEditing('mobile_number')}
                    rightAction={{
                      icon: isEditing('mobile_number') ? 'check' : 'edit',
                      label: isEditing('mobile_number') ? 'Done' : 'Edit',
                      onClick: () => toggleEdit('mobile_number'),
                    }}
                  />
                </div>

                {/* Address book — multiple saved service addresses. */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-xs font-bold text-[#908e8e] block">
                      Saved addresses
                    </label>
                    {addressFormMode === null ? (
                      <button
                        type="button"
                        onClick={openAddAddress}
                        className="flex items-center gap-1.5 text-[#8e2a2f] hover:text-[#7a2228] transition-colors font-['DM_Sans',sans-serif] text-[13px] font-bold"
                      >
                        <span className="material-icons !text-[18px]">add</span>
                        Add address
                      </button>
                    ) : null}
                  </div>

                  {addressesLoading ? (
                    <p className="text-[#908e8e] text-[13px] py-3">
                      Loading your addresses…
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {addresses.length === 0 && addressFormMode === null ? (
                        <div className="rounded-[10px] border border-dashed border-[#eee] p-6 text-center">
                          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px]">
                            You haven't saved any addresses yet. Add one so we
                            can match you with nearby professionals.
                          </p>
                        </div>
                      ) : null}

                      {addresses.map((a) => {
                        const busy = addressBusyId === a.id;
                        const composed = [a.address_line1, a.city, a.postcode]
                          .map((s) => (s ?? '').trim())
                          .filter(Boolean)
                          .join(', ');
                        return (
                          <div
                            key={a.id}
                            className={`rounded-[10px] border p-4 transition-colors ${
                              a.is_default
                                ? 'border-[#8e2a2f]/40 bg-[#8e2a2f]/[0.02]'
                                : 'border-[#eee]'
                            } ${busy ? 'opacity-60' : ''}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="material-icons text-[#8e2a2f] !text-[18px]">
                                    location_on
                                  </span>
                                  <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] truncate">
                                    {a.label?.trim() || a.address_line1}
                                  </p>
                                  {a.is_default ? (
                                    <span className="shrink-0 rounded-full bg-[#8e2a2f] text-white text-[10px] font-bold px-2 py-0.5 leading-[14px]">
                                      Default
                                    </span>
                                  ) : null}
                                </div>
                                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] leading-[19px] pl-[26px]">
                                  {composed || a.address_line1}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 mt-3 pl-[26px]">
                              {!a.is_default ? (
                                <button
                                  type="button"
                                  onClick={() => handleSetDefaultAddress(a.id)}
                                  disabled={busy}
                                  className="font-['DM_Sans',sans-serif] text-[12px] font-bold text-[#8e2a2f] hover:text-[#7a2228] transition-colors disabled:opacity-60"
                                >
                                  Set default
                                </button>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => openEditAddress(a)}
                                disabled={busy}
                                className="font-['DM_Sans',sans-serif] text-[12px] font-bold text-[#908e8e] hover:text-[#3a3335] transition-colors disabled:opacity-60"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => setPendingDeleteId(a.id)}
                                disabled={busy}
                                className="font-['DM_Sans',sans-serif] text-[12px] font-bold text-[#908e8e] hover:text-[#dc2626] transition-colors disabled:opacity-60"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Inline add / edit form. */}
                      {addressFormMode !== null ? (
                        <div className="rounded-[10px] border border-[#8e2a2f]/30 p-4 space-y-4">
                          <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[13px]">
                            {addressFormMode === 'new'
                              ? 'Add a new address'
                              : 'Edit address'}
                          </p>
                          <LocationAutocomplete
                            label="Address"
                            required
                            value={addressDraft.address_line1}
                            // Free-typing clears lat/lng so save forces a
                            // re-pick from the suggestions. Without lat/lng
                            // the booking radius search can't include it.
                            onChange={(v) =>
                              setAddressDraft((d) => ({
                                ...d,
                                address_line1: v,
                                latitude: null,
                                longitude: null,
                              }))
                            }
                            onPlaceSelected={(place) =>
                              setAddressDraft((d) => ({
                                ...d,
                                address_line1: place.description,
                                city: place.city ?? d.city,
                                postcode: place.postcode ?? d.postcode,
                                country_code:
                                  place.country_code?.toUpperCase() ??
                                  d.country_code,
                                latitude: place.latitude ?? null,
                                longitude: place.longitude ?? null,
                              }))
                            }
                            containerHeight="h-[60px]"
                            icon="location_on"
                          />
                          <FloatingLabelInput
                            label="Label (e.g. Home, Work)"
                            value={addressDraft.label}
                            onChange={(v) =>
                              setAddressDraft((d) => ({ ...d, label: v }))
                            }
                            containerHeight="h-[60px]"
                            bottomOnly={true}
                          />
                          <FloatingLabelInput
                            label="Address line 2 (optional)"
                            value={addressDraft.address_line2}
                            onChange={(v) =>
                              setAddressDraft((d) => ({
                                ...d,
                                address_line2: v,
                              }))
                            }
                            containerHeight="h-[60px]"
                            bottomOnly={true}
                          />
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <FloatingLabelInput
                              label="Town / City"
                              value={addressDraft.city}
                              onChange={(v) =>
                                setAddressDraft((d) => ({ ...d, city: v }))
                              }
                              containerHeight="h-[60px]"
                              bottomOnly={true}
                            />
                            <FloatingLabelInput
                              label="Postcode"
                              value={addressDraft.postcode}
                              onChange={(v) =>
                                setAddressDraft((d) => ({ ...d, postcode: v }))
                              }
                              containerHeight="h-[60px]"
                              bottomOnly={true}
                            />
                          </div>
                          <div className="flex items-center gap-3 pt-1">
                            <PrimaryButton
                              className="w-auto px-5 h-[44px] text-[13px]"
                              onClick={handleSaveAddress}
                              disabled={savingAddress}
                            >
                              {savingAddress
                                ? 'Saving…'
                                : addressFormMode === 'new'
                                  ? 'Add address'
                                  : 'Save changes'}
                            </PrimaryButton>
                            <button
                              type="button"
                              onClick={closeAddressForm}
                              disabled={savingAddress}
                              className="px-5 h-[44px] rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-[13px] bg-[#3a3335]/[0.04] hover:bg-[#3a3335]/[0.08] transition-all disabled:opacity-60"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="w-full lg:w-[361px] shrink-0">
              <div className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] p-8">
                <h3 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] mb-2">
                  General information
                </h3>
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px]">
                  Keep your details up to date so we can match you with the
                  right professionals and reach you about bookings. Your email
                  is used for sign-in and can't be changed here.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-12 flex items-center gap-4">
          <PrimaryButton
            className="w-auto px-6 h-[50px] text-[14px] whitespace-nowrap"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving…' : 'Update Settings'}
          </PrimaryButton>
          <button
            type="button"
            onClick={() => {
              setEditingField(null);
              load();
            }}
            className="px-8 h-[50px] rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-[14px] bg-[#3a3335]/[0.04] hover:bg-[#3a3335]/[0.08] transition-all"
          >
            Cancel
          </button>
        </div>
      </main>

      <Footer />

      <ConfirmModal
        isOpen={pendingDeleteId !== null}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={handleConfirmDeleteAddress}
        title="Remove this address?"
        message="This deletes the saved address from your address book. You can add it again at any time."
        confirmLabel="Delete"
        confirming={deletingAddress}
      />
    </div>
  );
}
